import { resolveStoredFileUrl } from "utils/storedFileUrl";
import PropTypes from "prop-types";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import {
	Card,
	Fade,
	Grid,
	Icon,
	IconButton,
	MenuItem,
	Modal,
	Select,
	Tooltip,
} from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import CustomPagination from "components/Pagination";
import DataTableCustom from "components/Tables/DataTableCustom";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";

import { valorAPesos } from "utils/formatters";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
import SoftButton from "components/SoftButton";
import ModalCargarPagoCC from "../ModalCargarPagoCC";
import pdfIcon from "assets/images/file-type-pdf2.svg";
import { UserContext } from "context/user";
import Factura from "layouts/eventos/components/Factura";

const LIMIT = 10;

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	maxHeight: "90%",
	width: { xs: "95%", sm: "85%", md: "75%", xxl: "70%" },
	overflowY: "auto",
	p: 3,
};

const GET_CC_INFO = gql`
	query getCCinfo($idClienteEvento: ID!, $filter: JSON, $pagination: PaginationInput) {
		paginationInfo {
			pages
			total
		}
		cuentaCorrienteClienteEvento(
			idClienteEvento: $idClienteEvento
			filter: $filter
			pagination: $pagination
		) {
			saldo
			totalCount
			items {
				__typename
				... on VentaEvento {
					id
					fechaFactura
					createdAt
					total
					estado
					numeroDeFacturaAfip
					tipoFactura
					nro_doc
					CAE
					CAEFchVto
					archivo
					idPresupuestoEvento
					url
					concepto
					descuento
					idAnulada
					imp_iva21
					subtotal21
					nroAnulado
					tipo_cbte
					tipo_doc
					pto_vta
					recargo
					cliente {
						nombre
						apellido
						tipoCliente
						tipoDocumento
						tipoResponsable
						documento
						email
						razonSocial
					}
					evento {
						id
						nombreEvento
					}
				}
				... on PagoEvento {
					id
					fecha
					createdAt
					total
					estado
					descripcion
					cuotas
					nombre
					banco
					nroTransferencia
					archivo
					idVentaEvento
				}
			}
		}
	}
`;

const DEFAULT_ORDER = { campo: "createdAt", orden: "DESC" };

const TIPO_OPTIONS = [
	{ value: "TODOS", label: "Todos" },
	{ value: "VENTAS", label: "Ventas" },
	{ value: "PAGOS", label: "Pagos" },
];

const formatDateTime = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("es-AR", {
		dateStyle: "short",
		timeStyle: "short",
	}).format(date);
};

const formatCurrency = (value) => valorAPesos(Number(value || 0));

function ModalCuentaCorrienteEventos({ open, handleClose, selected }) {
	const [page, setPage] = useState(1);
	const [tipo, setTipo] = useState("TODOS");
	const [orderBy, setOrderBy] = useState(DEFAULT_ORDER);
	const [openPagoModal, setOpenPagoModal] = useState(false);
	const [openFacturaModal, setOpenFacturaModal] = useState(false);
	const [facturaData, setFacturaData] = useState(null);
	const { user } = useContext(UserContext);

	const handleOpenFacturaModal = useCallback((venta) => {
		if (!venta) return;
		setFacturaData(venta);
		setOpenFacturaModal(true);
	}, []);

	const handleCloseFacturaModal = useCallback(() => {
		setOpenFacturaModal(false);
		setFacturaData(null);
	}, []);

	const variables = useMemo(() => {
		if (!selected?.id) return null;
		return {
			idClienteEvento: selected.id,
			pagination: { page, limit: LIMIT },
			filter: {
				campo: orderBy.campo,
				direccion: orderBy.orden,
				tipo: tipo === "TODOS" ? null : tipo,
			},
		};
	}, [selected?.id, page, orderBy, tipo]);

	const { data, loading, refetch } = useQuery(GET_CC_INFO, {
		variables: variables ?? undefined,
		skip: !open || !variables,
		fetchPolicy: "no-cache",
	});

	const saldo = data?.cuentaCorrienteClienteEvento?.saldo || 0;
	const totalCount = data?.cuentaCorrienteClienteEvento?.totalCount || 0;
	const items = data?.cuentaCorrienteClienteEvento?.items || [];
	const paginationInfo = data?.paginationInfo;
	const totalRecords = paginationInfo?.total ?? totalCount;
	const pages = paginationInfo?.pages ?? (totalRecords ? Math.ceil(totalRecords / LIMIT) : 0);

	useEffect(() => {
		if (!open) {
			setPage(1);
			setTipo("TODOS");
			setOrderBy(DEFAULT_ORDER);
			setOpenPagoModal(false);
			setOpenFacturaModal(false);
			setFacturaData(null);
		}
	}, [open]);

	const rows = useMemo(() => {
		if (!items?.length) return [];
		return items.map((item) => {
			const isVenta = item.__typename === "VentaEvento";
			const fechaBase = isVenta ? item.fechaFactura : item.fecha;
			const descripcion = isVenta
				? item.evento
					? `${item.evento.nombreEvento}`
					: "Factura emitida"
				: item.descripcion || "Pago realizado";
			return {
				id: item.id,
				__typename: item.__typename,
				tipo: isVenta ? "Venta" : "Pago",
				createdAt: item.createdAt,
				fecha: fechaBase,
				descripcion,
				estado: item.estado,
				total: item.total,
				saldoParcial: item.saldoParcial,
				detalle: item,
			};
		});
	}, [items]);

	const columns = useMemo(
		() => [
			{
				field: "tipo",
				headerName: "Tipo",
				width: "12%",
				renderCell: (row) => (
					<SoftTypography variant="caption" fontWeight="medium" color="dark">
						{row.tipo || "-"}
					</SoftTypography>
				),
			},
			{
				field: "createdAt",
				headerName: "Fecha",
				// width: "18%",
				align: "center",
				sortable: true,
				customSortValue: (item) => new Date(item.createdAt),
				renderHeader: (column, isCurrentSort, direction) => (
					<SoftBox
						display="flex"
						alignItems="center"
						gap={1}
						sx={{
							width: "100%",
							overflow: "hidden",
							minWidth: 0,
						}}
					>
						<SoftTypography
							variant="caption"
							fontWeight="bold"
							textTransform="uppercase"
							color="dark"
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								flex: 1,
								minWidth: 0,
							}}
							ml={2}
							title={column.headerName}
						>
							{column.headerName}
						</SoftTypography>
						{isCurrentSort && direction ? (
							<Icon
								fontSize="small"
								sx={{
									color: direction === "asc" ? "primary.main" : "primary.main",
									opacity: 0.8,
								}}
							>
								{direction === "asc" ? "arrow_drop_up" : "arrow_drop_down"}
							</Icon>
						) : (
							<Icon
								fontSize="small"
								sx={{
									color: "text.secondary",
									opacity: 0.4,
									"&:hover": { opacity: 0.8 },
								}}
							>
								import_export
							</Icon>
						)}
					</SoftBox>
				),
				renderCell: (row) => (
					<SoftTypography variant="caption" color="text">
						{formatDateTime(row.fecha || row.createdAt)}
					</SoftTypography>
				),
			},
			{
				field: "descripcion",
				headerName: "Descripción",
				// width: "32%",
				align: "center",
				sortable: false,
				renderCell: (row) => (
					<SoftTypography
						variant="caption"
						color="dark"
						sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
						title={row.descripcion || "-"}
					>
						{row.descripcion || "-"}
					</SoftTypography>
				),
			},
			{
				field: "estado",
				headerName: "Estado",
				// width: "14%",
				align: "center",
				renderCell: (row) => (
					<SoftTypography variant="caption" color="text">
						{row.estado || "-"}
					</SoftTypography>
				),
			},
			{
				field: "total",
				headerName: "Importe",
				// width: "12%",
				align: "center",
				sortable: false,
				renderCell: (row) => (
					<SoftTypography
						variant="caption"
						color={Number(row.total || 0) < 0 ? "error" : "success"}
						fontWeight="bold"
					>
						{formatCurrency(Math.abs(row.total))}
					</SoftTypography>
				),
			},
			{
				field: "archivo",
				headerName: "Archivo",
				align: "center",
				sortable: false,
				width: "13%",
				renderCell: (row) => {
					const hasArchivo = Boolean(row?.detalle?.archivo);
					const isFacturaDisponible = row.tipo === "Venta" && hasArchivo;
					const facturaEmitida = Boolean(row?.detalle?.CAE);

					if (isFacturaDisponible) {
						return (
							<SoftBox display="flex" justifyContent="center" alignItems="center">
								<IconButton
									size="small"
									color="uproGreen"
									onClick={(e) => {
										e.stopPropagation();
										if (facturaEmitida) {
											console.log('Opening factura modal for venta:', row.detalle);
											handleOpenFacturaModal(row.detalle);
										}
									}}
								>
									<Tooltip title={facturaEmitida ? "Ver factura" : "Factura pendiente"} placement="top">
										<Icon fontSize="small">receipt</Icon>
									</Tooltip>
								</IconButton>
							</SoftBox>
						);
					}

					return (
						<SoftBox display="flex" justifyContent="center" alignItems="center">
							<SoftButton
								variant="text"
								color="dark"
								size="medium"
								iconOnly
								circular
								onClick={(e) => {
									e.stopPropagation();
									if (hasArchivo) {
										const url = resolveStoredFileUrl(row?.detalle?.archivo);
										window.open(url, "_blank", "noopener,noreferrer");
									}
								}}
								sx={{
									minWidth: "auto",
									width: "48px",
									height: "48px",
									p: 1,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
								title={hasArchivo ? "Ver archivo" : "Sin archivo"}
							>
								<SoftBox
									component="img"
									src={pdfIcon}
									sx={{
										width: "100%",
										height: "100%",
										objectFit: "contain",
										filter: hasArchivo ? "none" : "grayscale(100%)",
										opacity: hasArchivo ? 1 : 0.45,
									}}
								/>
							</SoftButton>
						</SoftBox>
					);
				},
			},
		],
		[handleOpenFacturaModal]
	);

	const handleSortChange = (sort) => {
		if (!sort?.field || !sort?.direction) {
			setOrderBy(DEFAULT_ORDER);
			setPage(1);
			return;
		}

		setOrderBy({ campo: sort.field, orden: sort.direction.toUpperCase() });
		setPage(1);
	};

	return (
		<>
			<Modal aria-labelledby="modal-cuenta-corriente-eventos" open={open} onClose={handleClose} closeAfterTransition>
				<Fade in={open}>
					<Card sx={style}>
						<SoftBox>
							<SoftBox display="flex" justifyContent="space-between" alignItems="center">
								<SoftBox>
									<SoftTypography variant="h6">
										Cuenta corriente <strong>{selected?.nombre || selected?.razonSocial}</strong>
									</SoftTypography>
								</SoftBox>
								<SoftBox display="flex" justifyContent="space-between" alignItems="center">
									<SoftBox mr={2}>
										<SoftButton color="dark" circular onClick={() => setOpenPagoModal(true)} sx={{ mr: 2 }}>
											<Icon fontSize="small">add</Icon>
											Cargar Pago
										</SoftButton>
									</SoftBox>

									<SoftBox mr={2}>
										<Select
											value={tipo}
											onChange={(event) => {
												setTipo(event.target.value);
												setPage(1);
											}}
											fullWidth
										>
											{TIPO_OPTIONS.map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
												</MenuItem>
											))}
										</Select>
									</SoftBox>

									<SoftBox>
										<Tooltip title="Cerrar" placement="top">
											<Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
												close
											</Icon>
										</Tooltip>
									</SoftBox>
								</SoftBox>

							</SoftBox>

							<SoftBox mt={2} mb={2}>
								<Grid container spacing={2} alignItems="center">
									{/* <Grid item xs={12} sm={6} md={4}>
									<Card sx={{ p: 2 }}>
										<SoftTypography variant="button" color="text">
											Filtrar por tipo
										</SoftTypography>
										<Select
											value={tipo}
											onChange={(event) => {
												setTipo(event.target.value);
												setPage(1);
											}}
											fullWidth
											sx={{ mt: 1 }}
										>
											{TIPO_OPTIONS.map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
												</MenuItem>
											))}
										</Select>
									</Card>
								</Grid> */}
									<Grid item xs={12} sm={6} md={8}>
										<Card sx={{ p: 2 }}>
											<SoftBox display="flex" height="100%">
												<SoftBox p={2} display="flex" flexDirection="column" gap={1}>
													<SoftBox display="flex" alignItems="center" gap={1}>
														<SoftBox>
															<Card>
																<SoftBox
																	bgColor={saldo < 0 ? getUproColor("uproPink") : getUproColor("uproGreen")}
																	sx={{
																		width: "30px",
																		height: "30px",
																	}}
																	display="flex"
																	justifyContent="center"
																	alignItems="center"
																>
																	<Icon fontSize="small" sx={{ color: "#fff" }}>
																		{saldo < 0 ? "info" : "check"}
																	</Icon>
																</SoftBox>
															</Card>
														</SoftBox>

														{saldo < 0 ? (
															<SoftTypography variant="h6" fontWeight="regular">
																Saldo:{" "}
																<SoftTypography component="span" variant="h6" fontWeight="bold" color="error">
																	${formatMoneyPunto(Math.abs(saldo))}
																</SoftTypography>
															</SoftTypography>
														) : saldo > 0 ? (
															<SoftTypography variant="h6" fontWeight="regular">
																Saldo:{" "}
																<SoftTypography component="span" variant="h6" fontWeight="bold" color="uproGreen">
																	${formatMoneyPunto(saldo)}
																</SoftTypography>
															</SoftTypography>
														) : (
															<SoftTypography variant="h6" fontWeight="regular">
																Cuenta saldada{" "}
																<SoftTypography component="span" variant="h6" fontWeight="bold" color="primary">
																	${formatMoneyPunto(saldo)}
																</SoftTypography>
															</SoftTypography>
														)}
													</SoftBox>
												</SoftBox>
											</SoftBox>
										</Card>
									</Grid>
								</Grid>
							</SoftBox>

							<SoftBox py={2}>
								{loading ? (
									<Loading />
								) : items.length > 0 ? (
									<DataTableCustom
										headerStyles={{
											backgroundColor: "#ffffff", // fondo blanco
											borderBottom: "1.5px solid #070606ff",
											py: 1.1, // menos alto
											"& .MuiTypography-root": {
												fontWeight: 600, // negrita media
												color: "#344767", // gris azulado
												// textTransform: "none",                // sin uppercase
												fontSize: "0.7rem",
												letterSpacing: 0.2,
											},
										}}
										columns={columns}
										data={rows}
										loading={loading}
										showActions={false}
										sortable
										defaultSort={{ field: orderBy.campo, direction: orderBy.orden.toLowerCase() }}
										onSortChange={handleSortChange}
										maxHeight="50vh"
									/>
								) : (
									<SinDatos />
								)}
							</SoftBox>

							<CustomPagination
								loading={loading}
								length={rows.length}
								total={totalRecords}
								pages={pages}
								page={page}
								setPage={setPage}
								noCard
							/>
						</SoftBox>
					</Card>
				</Fade>
			</Modal>
			<Modal
				open={openFacturaModal}
				onClose={handleCloseFacturaModal}
				closeAfterTransition
				aria-labelledby="modal-factura-evento"
			>
				<Fade in={openFacturaModal}>
					<SoftBox
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							width: { xs: "95%", md: "85%" },
							maxHeight: "90%",
							overflowY: "auto",
							p: { xs: 1, md: 3 },
						}}
					>
						<Card>
							{facturaData && (
								<Factura factura={facturaData} empresa={user?.empresa} />
							)}
						</Card>
					</SoftBox>
				</Fade>
			</Modal>
			<ModalCargarPagoCC
				open={openPagoModal}
				onClose={() => setOpenPagoModal(false)}
				selected={selected}
				onSuccess={() => {
					setOpenPagoModal(false);
					refetch?.();
				}}
			/>
		</>
	);
}

ModalCuentaCorrienteEventos.propTypes = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
	selected: PropTypes.object,
};

export default ModalCuentaCorrienteEventos;
